import React, { FC, useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Flex } from "reflexbox";

import { useFormatMessage } from "@hooks/formatMessage";

import {
  AddButton,
  ScrollableList,
  ListColumn,
  ExpandableRow,
  ListContainer,
  Text,
  EditIcon,
  TrashIcon,
  Input,
  Filter,
  Modal,
  Checkbox,
} from "@icr/react.uicomponents";

import { useSwal } from "@hooks/useSwal";
import { ReactComponent as IconKey } from "@assets/icons/key-temp.svg";

import WebservicesModal from "./Modal/WebServicesModal";
import { deserialize } from "../../../api/jsonApiParser";
import { TDispatch, TRootState } from "../../../redux/types";
import WebServicesModalPassword from "./ModalPassword/WebServicesModalPassword";
import {
  setWebservicesPageNo,
  fetchWebServices,
  deleteWebservice,
  getWebserviceById,
} from "./ducks";

// import "../../../styles/index.scss";
// import "./WebServices.scss";

enum formMode {
  edit = "edit",
  password = "password",
}

const WebServices: FC = () => {
  const [modalContent, setModalContent] = useState(undefined);
  const [modalIsOpen, setModalIsOpen] = useState(undefined);
  const [addWebserviceForm, setAddWebserviceForm] = useState(undefined);
  const { swalConfirmationMessage, swalAlertWithCallback } = useSwal();
  const [showMoreSpinner, setShowMoreSpinner] = useState(false);
  const [genericTextFilter, setGenericTextFilter] = useState<string>("");

  const formatMessage = useFormatMessage();

  const dispatch: TDispatch = useDispatch();
  const { webServicesList, moreWebServices, webServicesPageNo } = useSelector(
    ({ WebServicesReducer: { webServices, moreWebServices, webServicesPageNo } }: TRootState) => ({
      webServicesList: webServices,
      moreWebServices,
      webServicesPageNo,
    }),
    shallowEqual
  );

  useEffect(() => {
    dispatch(fetchWebServices());
  }, [dispatch]);

  const handleLoadMore = () => {
    if (!showMoreSpinner && moreWebServices) {
      setShowMoreSpinner(true);
      dispatch(
        fetchWebServices(webServicesPageNo, 10, {
          GenericText: genericTextFilter,
        })
      ).then(() => {
        setShowMoreSpinner(false);
      });
    }
  };

  const filterWebServices = () => {
    dispatch(setWebservicesPageNo(1)).then(() => {
      dispatch(
        fetchWebServices(1, 10, {
          GenericText: genericTextFilter,
        })
      );
    });
  };

  const clearFilters = () => {
    setGenericTextFilter(null);

    dispatch(setWebservicesPageNo(1)).then(() => {
      dispatch(fetchWebServices(1, 10));
    });
  };

  const openModal = (content, message?, entry?) => {
    if (content !== formMode.password.toString()) {
      getWebserviceById(entry.id).then(res => {
        if (res.success) {
          setAddWebserviceForm(deserialize(res.data));
          setModalIsOpen(true);
          setModalContent(content);
        }
      });
    } else {
      setAddWebserviceForm(entry);
      setModalIsOpen(true);
      setModalContent(content);
    }
  };

  const saveHistoryAndFilterWebServices = () => {
    filterWebServices();
  };

  const closeModal = () => {
    setModalIsOpen(false);
    saveHistoryAndFilterWebServices();

    // TODO: this.props.fetchCompanies().then(res => this.setState({ parentCompanyList: res }));
    // TODO: change the location of the list to the modal component
  };

  const showSuccessMessage = (action, callbackFunction) => {
    swalAlertWithCallback(
      {
        text: `WebService has been ${action}!`,
        type: "success",
        confirmationText: "Ok!",
      },
      callbackFunction
    );
  };

  const remove = id => {
    swalConfirmationMessage({
      text: "Do you really want to remove this webservice?",
      type: "info",
      cancelationText: "Cancel",
      confirmationText: "Remove webservice",
    }).then(value => {
      if (value) {
        deleteWebservice(id).then(ret => {
          if (ret) showSuccessMessage("removed", saveHistoryAndFilterWebServices);
        });
      }
    });
  };

  return (
    <ListContainer>
      <Modal isOpen={modalIsOpen} onRequestClose={closeModal}>
        {modalIsOpen && modalContent !== formMode.password ? (
          <WebservicesModal closeModal={closeModal} initialData={addWebserviceForm} />
        ) : (
          <WebServicesModalPassword closeModal={closeModal} initialData={addWebserviceForm} />
        )}
      </Modal>

      <Flex pl={["10px", "80px"]} pr={["10px", 0]} height="100%" width="100%">
        <Flex flexDirection="column" width="100%">
          <AddButton
            id="addWebServiceBtn"
            p="0 32px"
            m="32px 0"
            label={formatMessage("app.addnewwebservice", "Add WebService")}
            onClick={() => {
              setAddWebserviceForm({});
              setModalContent(formMode.edit);
              setModalIsOpen(true);
            }}
          />
          <ScrollableList
            items={webServicesList}
            onLoadMore={handleLoadMore}
            noItemsFoundMessage={formatMessage("app.noRecordAvailable")}
            renderHeader={
              <Flex width="100%">
                <ListColumn>
                  <Text>{formatMessage("app.webserviceName", "Name")}</Text>
                </ListColumn>
                <ListColumn>
                  <Text>{formatMessage("app.endpointUrl", "Endpoint Url")}</Text>
                </ListColumn>
                <ListColumn>
                  <Text>{formatMessage("app.username", "Username")}</Text>
                </ListColumn>
                <ListColumn>
                  <Text>{formatMessage("app.webserviceBindingTypeName", "Binding Type")}</Text>
                </ListColumn>
                <ListColumn>
                  <Text>{formatMessage("app.authenticationTypeName", "Authentication Type")}</Text>
                </ListColumn>
                <ListColumn></ListColumn>
              </Flex>
            }
            renderItem={(item, index) => (
              <ExpandableRow
                key={"webServiceRow" + index}
                id={item.id as string}
                expandable={false}
                top={
                  <Flex width="100%">
                    <ListColumn>
                      <Text font="semiBold"> {item.webserviceName}</Text>
                    </ListColumn>
                    <ListColumn>
                      <Text>{item.endpointUrl}</Text>
                    </ListColumn>
                    <ListColumn>
                      <Text> {item.username}</Text>
                    </ListColumn>
                    <ListColumn>
                      <Text>{item.webserviceBindingTypeName}</Text>
                    </ListColumn>
                    <ListColumn>
                      <Text>{item.authenticationTypeName}</Text>
                    </ListColumn>
                    <ListColumn icon>
                      <EditIcon
                        id={"changeEntry" + item.id}
                        title="Edit WebService"
                        onClick={() => openModal(formMode.edit, "", item)}
                      />
                      <IconKey
                        id={"changePasswordEntry" + item.id}
                        title="Edit Password"
                        onClick={() => openModal(formMode.password, "", item)}
                      />
                      <TrashIcon
                        id={"deleteEntry" + item.id}
                        title="Delete WebService"
                        onClick={() => remove(item.id)}
                      />
                    </ListColumn>
                  </Flex>
                }
                more={
                  <Flex width="100%" mb="40px">
                    <ListColumn direction="column">
                      <Checkbox
                        id="TableDebuggingModeInput"
                        name="TableDebuggingModeInput"
                        readOnly={true}
                        value={(item.debuggingMode as string) || ""}
                        checked={item.debuggingMode as boolean}
                        label={formatMessage("app.debuggingMode", "Debugging Mode")}
                        wrapperProps={{ width: "20%" }}
                      />
                    </ListColumn>
                  </Flex>
                }
              />
            )}
          />
        </Flex>
        <Filter
          id="filterArea"
          searchButtonText={formatMessage("app.search", "Search")}
          onClickSearchButton={filterWebServices}
          clearButtonText={formatMessage("app.clear", "Clear")}
          onClickClearButton={clearFilters}
          open={true}
        >
          <Input
            id="dynamicSearch"
            label="Dynamic Filter"
            value={genericTextFilter}
            placeholder={formatMessage("app.anyfield", "Any Field")}
            onChange={e => setGenericTextFilter(e.target.value)}
          />
        </Filter>
      </Flex>
    </ListContainer>
  );
};

export default WebServices;
